import React, { useState, useEffect, useMemo } from "react";

const TinyCloudLanding = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const words = useMemo(() => ["user", "app", "agent", "network state"], []);

  useEffect(() => {
    const currentWord = words[currentWordIndex];
    const typingSpeed = isDeleting ? 50 : 200;
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing forward
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          // Word is complete, start deleting after a pause
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          // Word is deleted, move to next word
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex, words]);

  const RolodexWord = () => (
    <span className="typing-word">
      {displayText}
      <span className="cursor">|</span>
    </span>
  );

  useEffect(() => {
    // Mobile menu functionality
    const mobileToggle = document.querySelector('.mobile-menu-toggle');
    const navLinks = document.querySelector('.nav-links');
    
    const handleToggle = function() {
      if (navLinks) {
        navLinks.style.display = navLinks.style.display === 'flex' ? 'none' : 'flex';
        navLinks.style.flexDirection = 'column';
        navLinks.style.position = 'absolute';
        navLinks.style.top = '100%';
        navLinks.style.left = '0';
        navLinks.style.right = '0';
        navLinks.style.background = 'var(--pure-white)';
        navLinks.style.border = '2px solid var(--pure-black)';
        navLinks.style.borderTop = 'none';
        navLinks.style.padding = '1rem';
        navLinks.style.gap = '1rem';
      }
    };
    
    // Smooth scrolling for anchor links
    const handleAnchorClick = function(e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    };
    
    if (mobileToggle) {
      mobileToggle.addEventListener('click', handleToggle);
    }
    
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleAnchorClick);
    });
    
    // Cleanup
    return () => {
      if (mobileToggle) {
        mobileToggle.removeEventListener('click', handleToggle);
      }
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick);
      });
    };
  }, []);

  return (
    <div style={{
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      fontFamily: "'Helvetica Neue', Arial, sans-serif",
      lineHeight: 1.5,
      color: '#000000',
      background: '#FFFFFF',
      fontWeight: 400
    }}>
      <style>
        {`
        /* Reset and Base Styles */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        :root {
            --primary-red: #FF0000;
            --primary-blue: #0066CC;
            --primary-yellow: #FFCC00;
            --pure-black: #000000;
            --pure-white: #FFFFFF;
            --light-gray: #F8F8F8;
            --grid-gray: #E0E0E0;
        }

        body {
            font-family: 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.5;
            color: var(--pure-black);
            background: var(--pure-white);
            font-weight: 400;
        }

        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 2rem;
        }

        /* Typography */
        .section-badge {
            display: inline-block;
            background: var(--pure-white);
            color: var(--pure-black);
            padding: 0.75rem 2rem;
            border: 2px solid var(--pure-black);
            font-size: 0.8rem;
            font-weight: 700;
            margin-bottom: 2rem;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .section-title {
            font-size: clamp(2rem, 5vw, 3.5rem);
            font-weight: 700;
            color: var(--pure-black);
            margin-bottom: 2rem;
            line-height: 1.2;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .section-subtitle {
            font-size: clamp(1rem, 2.5vw, 1.2rem);
            color: var(--pure-black);
            max-width: 700px;
            margin: 0 auto;
            font-weight: 400;
            line-height: 1.6;
        }

        /* Header */
        .header {
            background: var(--pure-white);
            border-bottom: 4px solid var(--pure-black);
            position: sticky;
            top: 0;
            z-index: 100;
        }

        .nav {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem 0;
            gap: 3rem;
        }

        .logo {
            font-size: 1.5rem;
            font-weight: 900;
            color: var(--pure-black);
            text-decoration: none;
            letter-spacing: -0.5px;
            text-transform: uppercase;
        }

        .nav-links {
            display: flex;
            gap: 3rem;
            list-style: none;
            margin: 0 auto 0 0;
            padding: 0;
        }

        .nav-links a {
            text-decoration: none;
            color: var(--pure-black);
            font-weight: 700;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.2s ease;
            position: relative;
            padding: 0.25rem 0;
        }

        .nav-links a::after {
            content: '';
            position: absolute;
            bottom: -4px;
            left: 0;
            width: 0;
            height: 3px;
            background: var(--pure-black);
            transition: width 0.3s ease;
        }

        .nav-links a:hover::after {
            width: 100%;
        }

        .nav-cta {
            background: var(--pure-black);
            color: var(--pure-white);
            padding: 0.875rem 1.75rem;
            border: none;
            font-weight: 700;
            text-decoration: none;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.2s ease;
            white-space: nowrap;
        }

        .nav-cta:hover {
            background: var(--pure-white);
            color: var(--pure-black);
            outline: 3px solid var(--pure-black);
            outline-offset: -3px;
        }

        /* Hero Section */
        .hero {
            background: var(--pure-white);
            padding: 6rem 0;
            text-align: center;
        }

        .hero-badge {
            display: inline-block;
            background: var(--pure-black);
            color: var(--pure-white);
            padding: 0.75rem 2rem;
            font-size: 0.8rem;
            font-weight: 700;
            margin-bottom: 2rem;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .hero h1 {
            font-size: clamp(2rem, 5vw, 3.5rem);
            font-weight: 700;
            margin-bottom: 1rem;
            line-height: 1.1;
            color: var(--pure-black);
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .typing-word {
            color: var(--pure-black);
        }

        .cursor {
            color: var(--pure-black);
            animation: blink 1s infinite;
        }

        @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
        }

        .hero .tagline {
            font-size: clamp(1rem, 2.5vw, 1.2rem);
            color: var(--pure-black);
            margin-bottom: 3rem;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .hero-ctas {
            display: flex;
            gap: 0;
            justify-content: center;
            flex-wrap: wrap;
        }

        .cta-primary {
            background: var(--primary-red);
            color: var(--pure-white);
            padding: 1.25rem 2rem;
            border: 2px solid var(--pure-black);
            border-right: none;
            font-size: 0.9rem;
            font-weight: 700;
            text-decoration: none;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: background 0.2s ease;
        }

        .cta-primary:hover {
            background: var(--pure-black);
        }

        .cta-secondary {
            background: var(--pure-white);
            border: 2px solid var(--pure-black);
            color: var(--pure-black);
            padding: 1.25rem 2rem;
            font-size: 0.9rem;
            font-weight: 700;
            text-decoration: none;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: background 0.2s ease;
        }

        .cta-secondary:hover {
            background: var(--primary-yellow);
        }

        /* Value Proposition */
        .value-prop {
            background: var(--light-gray);
            border-top: 4px solid var(--pure-black);
            border-bottom: 4px solid var(--pure-black);
            padding: 4rem 0;
            text-align: center;
        }

        .value-prop p {
            font-size: clamp(1rem, 2.5vw, 1.2rem);
            color: var(--pure-black);
            font-weight: 500;
            line-height: 1.6;
            text-transform: uppercase;
            letter-spacing: 1px;
            max-width: 800px;
            margin: 0 auto;
        }

        /* Main Features */
        .main-features {
            padding: 6rem 0;
            background: var(--light-gray);
            border-bottom: 4px solid var(--pure-black);
        }

        .section-header {
            text-align: center;
            margin-bottom: 4rem;
        }

        .section-subheading {
            font-size: clamp(1.2rem, 3vw, 2rem);
            font-weight: 300;
            color: var(--pure-black);
            margin-bottom: 2rem;
            line-height: 1.3;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .feature-showcase {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-template-rows: repeat(2, 1fr);
            gap: 4px;
            margin-top: 4rem;
            min-height: 600px;
        }

        .feature-item {
            background: var(--pure-white);
            border: 2px solid var(--pure-black);
            padding: 3rem 2rem;
            transition: background 0.2s ease;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
        }

        .feature-item:nth-child(1) {
            border-right: none;
        }

        .feature-item:nth-child(3) {
            border-right: none;
        }

        .feature-item:hover {
            background: var(--light-gray);
        }

        .feature-item h3 {
            font-size: clamp(1rem, 2.5vw, 1.2rem);
            font-weight: 700;
            color: var(--pure-black);
            margin-bottom: 1.5rem;
            line-height: 1.3;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        .feature-item p {
            font-size: 0.9rem;
            color: var(--pure-black);
            margin-bottom: 1.5rem;
            line-height: 1.7;
            font-weight: 400;
            flex-grow: 1;
        }

        .feature-list {
            list-style: none;
            font-size: 0.85rem;
        }

        .feature-list li {
            color: var(--pure-black);
            margin: 0.5rem 0;
            padding-left: 1rem;
            position: relative;
            font-weight: 400;
        }

        .feature-list li::before {
            content: "■";
            position: absolute;
            left: 0;
            color: var(--pure-black);
            font-weight: bold;
        }

        /* Vision Section */
        .vision-section {
            background: var(--pure-white);
            padding: 8rem 0;
            text-align: center;
        }

        .vision-content {
            max-width: 800px;
            margin: 0 auto;
        }

        .vision-section .section-badge {
            background: var(--light-gray);
            border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .future-content p {
            font-size: clamp(1rem, 2.5vw, 1.1rem);
            line-height: 1.7;
            margin-bottom: 2rem;
            color: var(--pure-black);
            font-weight: 400;
        }

        .quote {
            background: var(--light-gray);
            color: var(--pure-black);
            border-radius: 8px;
            padding: 2rem;
            margin: 3rem auto;
            font-style: italic;
            font-size: clamp(1rem, 2.5vw, 1.1rem);
            font-weight: 500;
            max-width: 600px;
        }

        .quote-author {
            margin-top: 1.5rem;
            font-weight: 700;
            color: var(--pure-black);
            font-style: normal;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        /* Use Cases */
        .use-cases {
            padding: 6rem 0;
            background: var(--pure-white);
            border-bottom: 4px solid var(--pure-black);
        }

        .use-case-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 2rem;
            margin-top: 4rem;
        }

        .use-case {
            background: var(--pure-white);
            border: 2px solid var(--pure-black);
            padding: 2rem;
            transition: all 0.2s ease;
            position: relative;
        }

        .use-case::before {
            content: "";
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            height: 6px;
            background: var(--pure-black);
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.3s ease;
        }

        .use-case:hover::before {
            transform: scaleX(1);
        }

        .use-case:hover {
            background: var(--light-gray);
        }

        .use-case h4 {
            font-size: 1.1rem;
            font-weight: 700;
            margin-bottom: 1rem;
            color: var(--pure-black);
            line-height: 1.3;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        .use-case p {
            color: var(--pure-black);
            line-height: 1.6;
            font-weight: 400;
            font-size: 0.9rem;
        }

        /* Footer CTA */
        .footer-cta {
            background: var(--light-gray);
            padding: 6rem 0;
            border-bottom: 4px solid var(--pure-black);
            text-align: center;
        }

        .footer-cta h2 {
            font-size: clamp(2rem, 4vw, 2.5rem);
            font-weight: 700;
            color: var(--pure-black);
            margin-bottom: 1rem;
            line-height: 1.3;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .footer-cta p {
            font-size: clamp(1rem, 2.5vw, 1.1rem);
            color: var(--pure-black);
            margin-bottom: 3rem;
            font-weight: 400;
        }

        /* Footer */
        .footer {
            background: var(--pure-black);
            color: var(--pure-white);
            padding: 4rem 0 2rem;
        }

        .footer-content {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 3rem;
            margin-bottom: 3rem;
        }

        .footer-section h4 {
            font-weight: 700;
            margin-bottom: 2rem;
            color: var(--pure-white);
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 2px;
            border-bottom: 2px solid var(--primary-red);
            padding-bottom: 0.5rem;
            display: inline-block;
        }

        .footer-section:nth-child(2) h4 {
            border-bottom-color: var(--primary-blue);
        }

        .footer-section:nth-child(3) h4 {
            border-bottom-color: var(--primary-yellow);
        }

        .footer-links {
            list-style: none;
        }

        .footer-links li {
            margin: 1rem 0;
            border-bottom: 1px solid rgba(255,255,255,0.1);
            padding-bottom: 0.5rem;
        }

        .footer-links a {
            color: var(--pure-white);
            text-decoration: none;
            transition: color 0.2s ease;
            font-weight: 400;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        .footer-links a:hover {
            color: var(--primary-red);
        }

        .footer-section:nth-child(2) .footer-links a:hover {
            color: var(--primary-blue);
        }

        .footer-section:nth-child(3) .footer-links a:hover {
            color: var(--primary-yellow);
        }

        .footer-bottom {
            border-top: 4px solid var(--primary-red);
            padding-top: 2rem;
            text-align: center;
            color: var(--pure-white);
            font-weight: 400;
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        /* Mobile Navigation */
        .mobile-menu-toggle {
            display: none;
            background: none;
            border: 3px solid var(--pure-black);
            color: var(--pure-black);
            padding: 0.75rem 1rem;
            cursor: pointer;
            font-size: 1.2rem;
            font-weight: 700;
            transition: all 0.2s ease;
        }

        .mobile-menu-toggle:hover {
            background: var(--pure-black);
            color: var(--pure-white);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
            .container {
                padding: 0 1rem;
            }

            .nav-links {
                display: none;
            }

            .mobile-menu-toggle {
                display: block;
            }

            .hero {
                padding: 4rem 0;
            }

            .hero-ctas {
                flex-direction: column;
                align-items: center;
                gap: 0;
            }

            .cta-primary, .cta-secondary {
                width: 100%;
                max-width: 280px;
                text-align: center;
                border: 2px solid var(--pure-black);
                border-bottom: none;
            }

            .cta-secondary {
                border-bottom: 2px solid var(--pure-black);
            }

            .main-features, .vision-section, .use-cases {
                padding: 4rem 0;
            }

            .feature-showcase {
                grid-template-columns: 1fr;
            }

            .feature-item {
                border-right: 2px solid var(--pure-black) !important;
                border-bottom: none;
            }

            .feature-item:last-child {
                border-bottom: 2px solid var(--pure-black);
            }

            .use-case-grid {
                grid-template-columns: 1fr;
                gap: 1rem;
            }

            .footer-content {
                grid-template-columns: 1fr;
                gap: 2rem;
                text-align: center;
            }
        }

        @media (max-width: 480px) {
            .hero, .main-features, .vision-section, .use-cases, .footer-cta {
                padding: 3rem 0;
            }

            .feature-item, .use-case {
                padding: 1.5rem;
            }
        }
        `}
      </style>

      {/* Header */}
      <header className="header">
        <nav className="nav container">
          <a href="/" className="logo">TinyCloud</a>
          <ul className="nav-links">
            <li><a href="#platform">Platform</a></li>
            <li><a href="#use-cases">Use Cases</a></li>
            {/* <li><a href="#developers">Developers</a></li> */}
            <li><a href="https://docs.tinycloud.xyz">Docs</a></li>
          </ul>
          <a href="#contact" className="nav-cta hidden md:block">Get Started</a>
          <button className="mobile-menu-toggle" aria-label="Toggle mobile menu">☰</button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-badge">
            Building the future of user-owned data
          </div>
          <h1>
            Data infrastructure for <br />
            every <RolodexWord />.
          </h1>
          <p className="tagline">
            Your Data, Your Rules, Endless Possibilities
          </p>
          <div className="hero-ctas">
            <a href="https://docs.tinycloud.xyz" className="cta-primary">Read Documentation</a>
            <a href="/meet-sam" className="cta-secondary">Book a Meeting</a>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-prop">
        <div className="container">
          <p>
            TinyCloud is a new form of personal data infrastructure - where you own your data and grant access to the apps, agents, and AI tools you choose.
          </p>
        </div>
      </section>

      {/* Main Features */}
      <section id="platform" className="main-features">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Platform</div>
            <h2 className="section-title">Private capabilities.<br />Digital sovereignty.</h2>
            <div className="section-subheading">Own your digital territory</div>
            <p className="section-subtitle">
              Take control of your digital life with privacy-first applications and seamless data portability across all your tools.
            </p>
          </div>

          <div className="feature-showcase">
            <div className="feature-item">
              <h3>API Automations for Context Saving</h3>
              <p>Seamlessly capture and organize your digital context across platforms with intelligent automation that respects your privacy.</p>
              <ul className="feature-list">
                <li>Health wearables integration</li>
                <li>AI scribes for meeting notes</li>
                <li>Google Docs, Notion, Obsidian sync</li>
                <li>Voice memo processing</li>
              </ul>
            </div>

            <div className="feature-item">
              <h3>Advanced Data Protection</h3>
              <p>Your data is protected by cutting-edge access controls and our state-of-the-art policy engine. We've entered a strategic partnership with Aqua Protocol to implement the most advanced cryptographically verifiable data management tools available today. You decide what each app can access, when, and how.</p>
            </div>

            <div className="feature-item">
              <h3>Portable, Hyper-Personalized Context</h3>
              <p>Your digital reflection travels with you across applications. TinyCloud stores your data securely, making it accessible to any compatible app you choose to use.</p>
            </div>

            <div className="feature-item">
              <h3>Developer Platform</h3>
              <p>Simple APIs empower developers to build apps with memory capabilities, plus built-in safety rules to protect users. Create the next generation of applications that connect directly to user context.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="vision-section">
        <div className="container">
          <div className="vision-content">
            <div className="section-badge">Vision</div>
            <h2 className="section-title">The Future is User-Owned</h2>
            <div className="future-content">
              <p>
                We're building for the future of adaptive user interfaces - applications which suit the needs of users like custom tailored clothing. Your data can power the engine that builds custom interfaces tailored just for you.
              </p>
              
              <p>
                Corporate entities understand the value of your data and every day you sign it away in Terms of Service you don't even read. We're building the technology that unlocks the latent power of personal data with powerful new capabilities. Our digital identities are fragmented and disempowered - we're building the infrastructure that will power the next decade of computing.
              </p>

              <div className="quote">
                "TinyCloud empowers everyone to wield advanced technology—smart glasses, AI agents, BCIs—without middlemen."
                <div className="quote-author">— Sam Gbafa, Founder and CEO of TinyCloud</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="use-cases">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Applications</div>
            <h2 className="section-title">Use-Case Gallery</h2>
            <p className="section-subtitle">
              Discover how TinyCloud enables privacy-first applications across industries
            </p>
          </div>

          <div className="use-case-grid">
            <div className="use-case">
              <h4>Private Find My Friends</h4>
              <p>Location sharing that respects your privacy. Share your location with trusted contacts without corporate intermediaries tracking your every move.</p>
            </div>

            <div className="use-case">
              <h4>Personal Health Dashboard</h4>
              <p>Aggregate data from multiple health apps and wearables while maintaining complete privacy and control over your health information.</p>
            </div>

            <div className="use-case">
              <h4>AI-Powered Note Taking</h4>
              <p>Connect your voice memos, documents, and meeting notes with AI processing that happens on your terms, not a corporation's servers.</p>
            </div>

            <div className="use-case">
              <h4>Social Coordination Apps</h4>
              <p>Build group coordination apps where users retain control over their location and availability data without centralized tracking.</p>
            </div>

            <div className="use-case">
              <h4>Cross-Platform Productivity</h4>
              <p>Sync your workflow across Google Docs, Notion, Obsidian, and other tools while maintaining ownership of your content and ideas.</p>
            </div>

            <div className="use-case">
              <h4>Private Learning Assistants</h4>
              <p>AI tutors and learning assistants that adapt to your learning style using your personal data without compromising your privacy.</p>
            </div>

            <div className="use-case">
              <h4>Decentralized Social Networks</h4>
              <p>Social applications where you own your social graph, posts, and interactions - portable across platforms and services.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="footer-cta">
        <div className="container">
          <h2>Get started in minutes.<br />Go live in hours.</h2>
          <p>Join developers building the future of user-owned data.</p>
          <div className="hero-ctas">
            <a href="https://docs.tinycloud.xyz" className="cta-primary">Start Building</a>
            <a href="/meet-sam" className="cta-secondary">Book a Meeting</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Platform</h4>
              <ul className="footer-links">
                <li><a href="https://docs.tinycloud.xyz">Documentation</a></li>
                <li><a href="https://docs.tinycloud.xyz">API Reference</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Resources</h4>
              <ul className="footer-links">
                <li><a href="/protocol">Protocol</a></li>
                <li><a href="/labs">Labs</a></li>
                <li><a href="/privacy">Privacy Policy</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Connect</h4>
              <ul className="footer-links">
                <li><a href="https://twitter.com/TinyCloudLabs">X/Twitter</a></li>
                <li><a href="https://github.com/TinyCloudLabs/web-sdk">GitHub</a></li>
                <li><a href="https://t.me/+pplkv1XbbU01MDVh">Telegram</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 TinyCloud. Building the future of user-owned data.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TinyCloudLanding;